import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import userPlaceholder from "../../../assets/images/userPlaceholder.png";
import { selectUser } from "../../../core/auth/authSlice";
import { User } from "../../../shared/models";
import { getProfilePictureUrl } from "../../../shared/utils/profilePictureUrl";
import { capitalizeName } from "../../../shared/utils/util";
import FollowButton from "../btns/follow-btn/FollowButton";
import { MsgButton } from "../btns/msg-btn/MsgButton";
import "./UserCard.scss";

export interface UserPreviewInfo {
	id: string;
	sport: string;
	avatarVersion: number | null;
	profilePictureUrl?: string | null;
	isFollowed: boolean;
	accountType?: string;
	name: string;
	surname: string;
}

export interface UserCardProps {
	userInfo: UserPreviewInfo | undefined;
	action?: "follow" | "message" | null;
	className?: string;
	size?: "s";
	darkMode?: boolean;
	onClick?: () => void;
	onActionClick?: () => void;
}

const UserCard: React.FC<UserCardProps> = ({
	userInfo,
	className,
	action,
	size,
	darkMode,
	onClick,
	onActionClick,
}) => {
	const navigate = useNavigate();
	const { actorId, avatarVersion } = useSelector(selectUser) as User;
	const { t } = useTranslation();

	if (!userInfo) return <></>;

	const selectSize = (): { image: number; gap: number; text: number } => {
		if (!size) return { image: 60, gap: 15, text: 16 };
		else
			switch (size) {
				// case "l":
				//   return 40;
				// case "m":
				//   return 30;
				case "s":
					return { image: 35, gap: 3, text: 13 };
			}
	};

	const sizeStyle = {
		"--image-size": `${selectSize().image}px`,
		"--gap": `${selectSize().gap}px`,
		"--text-size": `${selectSize().text}px`,
		"--name-color": darkMode ? "black" : "white",
		"--sport-color": darkMode ? "#3a4f50" : "#9cb6b7",
		lineHeight: size === "s" ? "normal" : "unset",
	} as React.CSSProperties;

	const clickUser = () => {
		// Navigate to /u/:userId - the ProfileResolver will handle team redirects
		navigate(`/u/${userInfo.id}`);
	};

	const setAction = () => {
		if (actorId !== userInfo.id) {
			switch (action) {
				case "follow":
					return (
						<FollowButton
							follow_id={userInfo.id}
							followed={userInfo.isFollowed}
							size="m"
						/>
					);
				case "message":
					return (
						<MsgButton
							memberId={userInfo.id}
							size="m"
							onMessageClick={onActionClick}
						/>
					);
			}
		}
	};

	return (
		<div
			style={sizeStyle}
			className={`user-card ${className}`}
		>
			<div
				className="post__header-person-container"
				onClick={onClick ?? clickUser}
			>
				<img
					className="pic"
					src={
						getProfilePictureUrl(
							userInfo.id,
							actorId === userInfo.id ? avatarVersion : userInfo.avatarVersion,
							userInfo.profilePictureUrl
						) || userPlaceholder
					}
					alt="profile picture"
					onError={(e) => (e.currentTarget.src = userPlaceholder)}
				/>

				<div className="name-container">
					<p className={`name`}>
						{capitalizeName(userInfo.name)} {capitalizeName(userInfo.surname)}
					</p>
					<p className="sport">
						{t(`sportData.sportsLabels.${userInfo.sport}`)}
					</p>
				</div>
			</div>
			{setAction()}

			{/* <Dots /> */}
		</div>
	);
};

export default UserCard;
